<script lang="ts">
  import { getContext } from 'svelte';
  import { VideoPlayback } from '@squareup/public-web-design-system';
  import DynamicChildrenComponent from '$lib/components/DynamicChildrenComponent/DynamicChildrenComponent.svelte';
  import VideoTriggerPartial from '$components/common/partials/VideoTriggerPartial/VideoTriggerPartial.svelte';
  import ImageWithLinkPartial from '$components/common/partials/ImageWithLinkPartial/ImageWithLinkPartial.svelte';
  import VideoPlayIconPartial from '$components/common/partials/VideoPlayPartial/VideoPlayIconPartial.svelte';
  import multiColumnLayoutToColSpan from '$lib/helpers/multi-column-layout-to-col-span';
  import { DesignSystem as DesignSystemEnum } from '$lib/helpers/design-system';
  import type { TextUnitComponentProps } from './types';

  type $$Props = TextUnitComponentProps;

  export let entryId: $$Props['entryId'];
  export let title: $$Props['title'] = undefined;
  export let body: $$Props['body'] = undefined;
  export let links: $$Props['links'] = undefined;
  export let icon: $$Props['icon'] = undefined;
  export let headingLevel: $$Props['headingLevel'] = undefined;
  export let bodyFontSize: $$Props['bodyFontSize'] = undefined;
  export let textAlignment: $$Props['textAlignment'] = undefined;
  export let linkId: $$Props['linkId'] = undefined;
  export let video: $$Props['video'] = undefined;
  export let hasMedia: $$Props['hasMedia'] = undefined;
  export let hasVideoOrAnimation: $$Props['hasVideoOrAnimation'] = undefined;
  export let customCssClass: $$Props['customCssClass'];
  export let aproposImage: $$Props['aproposImage'] = undefined;
  export let image: $$Props['image'] = undefined;
  export let imageLinkUrl: $$Props['imageLinkUrl'] = undefined;
  export let imageLinkAriaLabel: $$Props['imageLinkAriaLabel'] = undefined;
  export let imageWidth: $$Props['imageWidth'] = undefined;
  export let imageInline: $$Props['imageInline'] = undefined;
  export let modal: $$Props['modal'] = undefined;
  export let animation: $$Props['animation'] = undefined;
  export let videoUrl: $$Props['videoUrl'] = undefined;
  export let videoText: $$Props['videoText'] = undefined;
  export let videoIconSize: $$Props['videoIconSize'] = undefined;
  export let videoIconStyle: $$Props['videoIconStyle'] = undefined;
  export let colSpan: $$Props['colSpan'] = undefined;
  export let columns: $$Props['columns'] = undefined;
  export let videoPlayButtonTitle: $$Props['videoPlayButtonTitle'] = undefined;

  const designSystem = getContext<DesignSystemEnum>('designSystem');

  const colSpanClasses = colSpan && columns ? multiColumnLayoutToColSpan(columns as number, colSpan) : '';
</script>

<div
  class="TextUnitComponent TextUnitComponent-{entryId}-box-properties {customCssClass} {colSpanClasses}"
  data-entry-id={entryId}
  id={linkId}
>
  <div class="gridWrapper">
    {#if hasMedia}
      <div class="mediaContainer">
        {#if hasVideoOrAnimation}
          <DynamicChildrenComponent children={[animation || video]} />
        {:else}
          <ImageWithLinkPartial {image} {aproposImage} {imageLinkUrl} {imageLinkAriaLabel} {imageWidth} {imageInline} />
        {/if}
        {#if videoUrl}
          <VideoTriggerPartial {videoUrl} title={videoPlayButtonTitle}>
            <svelte:fragment slot="video-player-link-content">
              {#if designSystem === DesignSystemEnum.PublicWebDesignSystem}
                <VideoPlayback />
              {:else}
                <VideoPlayIconPartial iconSize={videoIconSize} iconStyle={videoIconStyle} />
              {/if}
              {#if videoText}
                <p>{videoText}</p>
              {/if}
            </svelte:fragment>
          </VideoTriggerPartial>
        {/if}
      </div>
    {/if}
    {#if icon}
      <div class="iconContainer">
        <!-- eslint-disable-next-line svelte/no-at-html-tags - icon is imported from public-web-icons -->
        {@html icon}
      </div>
    {/if}
    <div class="heading {`font-size-${bodyFontSize}`}">
      {#if title}
        <svelte:element
          this={headingLevel}
          class="{headingLevel} balance-text"
          style:text-align={textAlignment}
          data-title-for={entryId}
        >
          {title}
        </svelte:element>
      {/if}
      {#if body}
        <!-- eslint-disable-next-line svelte/no-at-html-tags - body is sanitized using richTextRendererHelper in text-unit-component-view-model.ts -->
        {@html body}
      {/if}
      {#if links?.length}
        <div>
          <DynamicChildrenComponent children={links} />
        </div>
      {/if}
    </div>
    {#if modal}
      <DynamicChildrenComponent children={modal} />
    {/if}
  </div>
</div>

<style lang="scss">
  @use '@squareup/public-web-design-system/dist/styles/xms-fe/mixins/grid.scss' as grid;

  @layer standard-components {
    .gridWrapper {
      @include grid.auto;
      @include grid.space(20);
    }
    .heading {
      font-family: var(--font-family-base);
      line-height: var(--line-height-body);
      font-size: var(--font-size-body);
      font-weight: 400;
      vertical-align: baseline;

      h6:not(:last-child) {
        margin-bottom: 0;
      }

      :global(h6 + p) {
        margin-top: 0;
      }
    }

    .font-size {
      &-small {
        line-height: var(--line-height-body-extra-small);
        font-size: var(--font-size-body-extra-small);
      }
      &-tiny {
        line-height: var(--line-height-body-extra-small);
        font-size: var(--font-size-body-extra-small);
      }
      &-medium {
        line-height: var(--line-height-body);
        font-size: var(--font-size-body);
      }
      &-large {
        line-height: var(--line-height-body-large);
        font-size: var(--font-size-body-large);
      }
    }

    .mediaContainer {
      position: relative;
      margin-left: auto;
      margin-right: auto;
      :global(.video-player-link) {
        position: absolute;
        transform: translate(-50%, -50%);
        left: 50%;
        top: 50%;
        text-align: center;
        color: var(--color-white);
        font-weight: var(--font-weight-medium);
        text-decoration: none;

        :global(.iconPlay:hover) {
          background-color: var(--color-blue-dark);
        }
      }
    }

    .iconContainer {
      flex-shrink: 0;
    }

    .TextUnitComponent {
      :global(.ModalComponent) {
        text-align: inherit;
        margin-top: 0;
        :global(.trigger) {
          flex-direction: row-reverse;
          padding-left: 0;
          padding-right: 0;
        }
        :global(.trigger-icon) {
          margin-right: 0.5rem;
        }
      }
    }
  }
</style>
